import React, { useEffect, useState } from "react";
import { Save, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function AdminEntra() {
  const [cfg, setCfg] = useState(null);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/entra");
      setCfg({ ...data, allowed_tenants_str: (data.allowed_tenants || []).join(", "), client_secret_changed: false });
    })();
  }, []);

  if (!cfg) return null;

  const save = async () => {
    try {
      const payload = {
        enabled: cfg.enabled,
        tenant_id: cfg.tenant_id,
        client_id: cfg.client_id,
        client_secret: cfg.client_secret_changed ? cfg.client_secret : "••••••••",
        redirect_uri: cfg.redirect_uri,
        allowed_tenants: cfg.allowed_tenants_str.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.put("/admin/entra", payload);
      toast.success("Entra configuration saved");
      setCfg({ ...cfg, client_secret_changed: false, client_secret: "••••••••" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-8 max-w-3xl">
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">// entra (azure ad)</div>
      <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Microsoft Entra ID</h1>
      <p className="text-sm text-zinc-400 mt-2">
        Configure your Entra App Registration to enable Microsoft sign-in and silent SSO.
      </p>

      <Card className="bg-zinc-900/60 border-zinc-800 p-6 mt-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Enable Microsoft Entra sign-in</Label>
            <div className="text-xs text-zinc-500 mt-1">When enabled, users see the &ldquo;Sign in with Microsoft&rdquo; option.</div>
          </div>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="entra-enabled-toggle" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Tenant ID</Label>
            <Input value={cfg.tenant_id} onChange={(e) => setCfg({ ...cfg, tenant_id: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1 font-mono text-xs" placeholder="common or {tenant-uuid}" data-testid="entra-tenant-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Client ID</Label>
            <Input value={cfg.client_id} onChange={(e) => setCfg({ ...cfg, client_id: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1 font-mono text-xs" data-testid="entra-clientid-input" />
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Client Secret</Label>
          <div className="relative mt-1">
            <Input
              type={reveal ? "text" : "password"}
              value={cfg.client_secret}
              onChange={(e) => setCfg({ ...cfg, client_secret: e.target.value, client_secret_changed: true })}
              onFocus={() => { if (!cfg.client_secret_changed) setCfg({ ...cfg, client_secret: "", client_secret_changed: true }); }}
              className="bg-zinc-950 border-zinc-800 font-mono text-xs pr-10"
              data-testid="entra-secret-input"
            />
            <button type="button" onClick={() => setReveal(!reveal)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-zinc-800">
              {reveal ? <EyeOff className="w-4 h-4 text-zinc-400" /> : <Eye className="w-4 h-4 text-zinc-400" />}
            </button>
          </div>
          <div className="text-xs text-zinc-600 mt-1">Stored encrypted at rest. Never returned to the client after save.</div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Redirect URI (whitelisted in Entra)</Label>
          <Input value={cfg.redirect_uri} onChange={(e) => setCfg({ ...cfg, redirect_uri: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1 font-mono text-xs" placeholder={`${window.location.origin}/auth/entra/callback`} data-testid="entra-redirect-input" />
          <div className="text-xs text-zinc-600 mt-1">Must match exactly what&apos;s configured in your Entra App Registration.</div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Allowed tenants (optional)</Label>
          <Input value={cfg.allowed_tenants_str} onChange={(e) => setCfg({ ...cfg, allowed_tenants_str: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1 font-mono text-xs" placeholder="tenant-uuid-1, tenant-uuid-2" data-testid="entra-allowed-tenants-input" />
          <div className="text-xs text-zinc-600 mt-1">Comma-separated. Leave empty to use Tenant ID above.</div>
        </div>

        <div className="pt-2">
          <Button onClick={save} className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="entra-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> Save configuration
          </Button>
        </div>
      </Card>

      <Card className="bg-zinc-900/40 border-zinc-800 p-5 mt-6">
        <h3 className="font-display text-sm font-medium mb-2">Setup checklist</h3>
        <ol className="space-y-1.5 text-xs text-zinc-400 list-decimal list-inside font-mono">
          <li>Register a Single-Page App in Entra Portal</li>
          <li>Add redirect URI: <span className="text-zinc-200">{window.location.origin}/auth/entra/callback</span></li>
          <li>Enable ID tokens and access tokens</li>
          <li>Add API permissions: openid, profile, email, User.Read</li>
          <li>Generate a client secret (server-side flow) and paste above</li>
        </ol>
      </Card>
    </div>
  );
}
